import ApiServiceV1 from '@/api/adminApi/AdminApiV1'
import { useCartStore } from '@/stores/CartStore'
import { useModalStore } from '@/stores/ModalStore'
import type { Address, AppliedVoucher, Cart, CartDeposit, CartMix, CartProduct, ContactData, DeliveryInformation, DiscountItem, SendWithClubPackage } from '@/types/cart';
import { isAxiosError } from 'axios';

export class CartService {
  private cartStore = useCartStore()
  private modalStore = useModalStore()

  async addProduct(productId: string, quantity: number) {
    const response = await ApiServiceV1.post('cart_items', {
      product_id: productId,
      quantity,
    })
    await this.cartStore.handleCartToken(response.data.cart_token)
    return response
  }

  // TODO vytvořit mix type
  async addMix(mix: Record<string, unknown>, kind: string, quantity: number) {
    const response = await ApiServiceV1.post('cart_mixes', {
      mix,
      kind,
      quantity,
    })

    await this.cartStore.handleCartToken(response.data.cart_token)
    return response
  }

  // TODO vytvořit mix type
  async updateMix(mix: Record<string, unknown>, kind: string, quantity: number, hash: string) {
    const response = await ApiServiceV1.put(`mixes/${hash}`, {
      mix,
      kind,
      quantity,
    })

    await this.cartStore.handleCartToken(response.data.cart_token)
    return response
  }

  async duplicateAndAddMix(hash: string, quantity: number) {
    const response = await ApiServiceV1.post(`mixes/add-to-cart`, {
      hash,
      quantity,
    })

    await this.cartStore.handleCartToken(response.data.cart_token)
    return response
  }

  async changeProductQuantity(productId: string, quantity: number) {
    const response = await ApiServiceV1.put(`cart_items/${productId}`, { quantity })

    await this.cartStore.handleCartToken(response.data.cart_token)
    return response
  }

  async changeMixQuantity(mixID: number, quantity: number) {
    const response = await ApiServiceV1.put(`cart_mixes/${mixID}`, { quantity })

    await this.cartStore.handleCartToken(response.data.cart_token)
    return response
  }

  async deleteProduct(productId: string) {
    const response = await ApiServiceV1.delete(`cart_items/${productId}`)

    await this.cartStore.handleCartToken(response.data.cart_token)
    return response
  }

  async deleteMix(mixId: string) {
    const response = await ApiServiceV1.delete(`cart_mixes/${mixId}`)

    await this.cartStore.handleCartToken(response.data.cart_token)
    return response
  }

  async changeItemTube(itemId: string, tubeUuid?: string) {
    const response = await ApiServiceV1.put('cart_items/change-tube', {
      id: itemId,
      tube_uuid: tubeUuid,
    })

    await this.cartStore.handleCartToken(response.data.cart_token)
    return response
  }

  async changeMixTube(mixId: string, tubeUuid: string) {
    const response = await ApiServiceV1.put('cart_mixes/change-tube', {
      id: mixId,
      tube_uuid: tubeUuid,
    })

    await this.cartStore.handleCartToken(response.data.cart_token)
    return response
  }

  async renameMix(mixId: number, name: string) {
    const response = await ApiServiceV1.put('cart_mixes/rename-mix', {
      id: mixId,
      name,
    })

    await this.cartStore.handleCartToken(response.data.cart_token)
    return response
  }

  async setDiscountCode(code: string) {
    try {
      const response = await ApiServiceV1.post('cart/set-discount-code', { code })

      await this.cartStore.handleCartToken(response.data.cart_token)
      this.modalStore.openModal({
        id: 'modal-alert',
        title: response.data.msg,
      })
      return response
    } catch (error) {
      if (isAxiosError(error)) {
        this.modalStore.openModal({
          id: 'modal-alert',
          title: error.response!.data.title,
          params: {
            message: error.response!.data.msg,
          },
        })
      }

      return
    }
  }

  async setCreditDiscount(useCredits: boolean) {
    try {
      const response = await ApiServiceV1.post('cart/set-credit-discount', { use_credits: useCredits })

      await this.cartStore.handleCartToken(response.data.cart_token)
      return response
    } catch (error) {
      if (isAxiosError(error)) {
        this.modalStore.openModal({
          id: 'modal-alert',
          title: error.response!.data.title,
          params: {
            message: error.response!.data.msg,
          },
        })
      }

      return
    }
  }

  async setFavorite(mixId: number, favorite: boolean) {
    const response = await ApiServiceV1.put(`customer/favorite_mixes/${mixId}`, {
      favorite,
    })
    this.cartStore.updateMixFavorite({
      mixID: response.data.mixID,
      isFavorite: response.data.isFavorite,
    })

    return response
  }

  getCartObject(cart?: Record<string, unknown>): Cart {
    return {
      id: cart?.id as number | undefined ?? 0,
      immediateDelivery: cart?.immediateDelivery as boolean | undefined ?? false,
      carrierHandoverDate: cart?.carrierHandoverDate as string ?? '',
      expectedDeliveryDate: cart?.expectedDeliveryDate as string ?? '',
      totalPrice: Number.parseFloat(cart?.totalPrice as string) ?? 0,
      totalPriceWithCredits: Number.parseFloat(cart?.totalPriceWithCredits as string) ?? 0,
      totalPriceWithoutVat: Number.parseFloat(cart?.totalPriceWithoutVat as string) ?? 0,
      totalVat: Number.parseFloat(cart?.totalVat as string) ?? 0,
      productPrice: Number.parseFloat(cart?.productPrice as string) ?? 0,
      productPriceWithoutVat: Number.parseFloat(cart?.productPriceWithoutVat as string),
      currency: cart?.currency as string | undefined ?? '',
      currencyCode: cart?.currencyCode as string | undefined ?? '',
      affiliateCredits: cart?.affiliateCredits as number | undefined ?? 0,
      affiliateNextCredits: cart?.affiliateNextCredits as number | undefined ?? 0,
      affiliatePercent: cart?.affiliatePercent as number | undefined ?? 0,
      affiliateNextPercent: cart?.affiliateNextPercent as number | undefined ?? 0,
      affiliateMissingToStepUp: cart?.affiliateMissingToStepUp as number | undefined ?? 0,
      hasFreeTransport: cart?.hasFreeTransport as boolean | undefined ?? false,
      inPlusClub: cart?.inPlusClub as boolean | undefined ?? false,
      itemsUntilFreeTransport: Number.parseInt(cart?.itemsUntilFreeTransport as string) ?? 0,
      priceUntilFreeTransport: Number.parseFloat(cart?.priceUntilFreeTransport as string) ?? 0,
      useCredits: cart?.useCredits as boolean | undefined ?? false,
      onlyVirtualProducts: cart?.onlyVirtualProducts as boolean | undefined ?? false,
      credits: Number.parseFloat(cart?.credits as string) ?? 0,
      usedCredits: Number.parseFloat(cart?.usedCredits as string) ?? 0,
      selectedTransport: cart?.selectedTransport,
      selectedPayment: cart?.selectedPayment ?? null,
      selectedBillingAddress: cart?.orderAsGift ? true : cart?.hasBillingAddress as boolean | undefined ?? false,
      orderAsGift: cart?.orderAsGift as boolean | undefined ?? false,
      selectedBuyingForCompany: !!(cart?.ic || cart?.dic),
      giftMessage: cart?.giftMessage as string | undefined ?? null,
      sendWithClubPackage: cart?.sendWithClubPackage
        ? {
            sendTogether: (cart?.sendWithClubPackage as Partial<SendWithClubPackage>).sendTogether,
            packageID: (cart?.sendWithClubPackage as Partial<SendWithClubPackage>).packageID,
            clubMembershipName: (cart?.sendWithClubPackage as Partial<SendWithClubPackage>).clubMembershipName,
            closeOrderAt: (cart?.sendWithClubPackage as Partial<SendWithClubPackage>).closeOrderAt,
            deliveryStreet: (cart?.sendWithClubPackage as Partial<SendWithClubPackage>).deliveryStreet,
            deliveryCity: (cart?.sendWithClubPackage as Partial<SendWithClubPackage>).deliveryCity,
          }
        : null,
      deliveryInformation: cart?.deliveryInformation
        ? {
            branch_id: (cart?.deliveryInformation as Partial<DeliveryInformation>).branch_id,
            branch_name: (cart?.deliveryInformation as Partial<DeliveryInformation>).branch_name,
            branch_street: (cart?.deliveryInformation as Partial<DeliveryInformation>).branch_street,
            branch_city: (cart?.deliveryInformation as Partial<DeliveryInformation>).branch_city,
            branch_zip: (cart?.deliveryInformation as Partial<DeliveryInformation>).branch_zip,
            branch_country: (cart?.deliveryInformation as Partial<DeliveryInformation>).branch_country,
          }
        : null,
      contactData: cart?.shippingAddress
        ? {
            firstName: (cart?.shippingAddress as Partial<ContactData>).firstName,
            lastName: (cart?.shippingAddress as Partial<ContactData>).lastName,
            email: (cart?.shippingAddress as Partial<ContactData>).email,
            phonePrefix: (cart?.shippingAddress as Partial<ContactData>).phonePrefix,
            phone: (cart?.shippingAddress as Partial<ContactData>).phone,
          }
        : null,
      shippingAddress: cart?.shippingAddress
        ? {
            street: (cart?.shippingAddress as Partial<Address>).street,
            city: (cart?.shippingAddress as Partial<Address>).city,
            zip: (cart?.shippingAddress as Partial<Address>).zip,
            country: (cart?.shippingAddress as Partial<Address>).country,
            company: (cart?.shippingAddress as Partial<Address>).company,
          }
        : null,
      billingContactData: cart?.billingAddress
        ? {
            firstName: (cart?.billingAddress as Partial<ContactData>).firstName,
            lastName: (cart?.billingAddress as Partial<ContactData>).lastName,
            email: (cart?.billingAddress as Partial<ContactData>).email,
            phonePrefix: (cart?.billingAddress as Partial<ContactData>).phonePrefix,
            phone: (cart?.billingAddress as Partial<ContactData>).phone,
          }
        : null,
      billingAddress: cart?.billingAddress
        ? {
            street: (cart?.billingAddress as Partial<Address>).street,
            city: (cart?.billingAddress as Partial<Address>).city,
            zip: (cart?.billingAddress as Partial<Address>).zip,
            country: (cart?.billingAddress as Partial<Address>).country,
            company: (cart?.billingAddress as Partial<Address>).company,
          }
        : null,
      cartProducts: cart?.cartProducts as CartProduct[] | undefined ?? [],
      cartDeposits: cart?.cartDeposits as CartDeposit[] | undefined ?? [],
      cartMixes: cart?.cartMixes as CartMix[] | undefined ?? [],
      appliedVouchers: cart?.appliedVouchers as AppliedVoucher[] | undefined ?? [],
      discountItems: cart?.discountItem ? [cart?.discountItem as DiscountItem] : [],
      orderGift: cart?.orderGift ?? null,
    }
  }
}
